/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * Pure Monte Carlo simulator (human-physics duty-cycle model).
 * This mirrors the existing engine behavior at a high level:
 *  - Poisson arrivals per persona based on share of total concurrency
 *  - Time-stepped state machine: reading -> thinking -> generating
 *  - Multi-turn conversations are already collapsed into per-turn tokens and serviceTimeSec
 *  - Generates p50/p95 active session curves per persona and aggregate
 */

export interface McPersonaInput {
  personaId: string;
  population: number;
  tokensPerTurn: {
    input: number;
    output: number;
    total: number;
  };
  dutyCycle: {
    readTimeSec: number;
    thinkTimeSec: number;
  };
  service: {
    ttftMs: number;
    decodeTokPerSec: number;
    serviceTimeSec: number;
  };
}

export interface McInput {
  personas: McPersonaInput[];
  concurrentUsersTotal: number;
  conversationsPerUser: number; // kept for compatibility, not used for scaling lambda
  latencySloMs: number;
  batchSize: number;
  trials: number;
  durationSec: number;
  trafficVolatility?: number;
}

export interface McPersonaStats {
  personaId: string;
  p50ActiveSessions: number;
  p95ActiveSessions: number;
  percentiles?: Record<McPercentile, number>;
}

export interface McResult {
  requestId: string;
  scenarioHash: string;
  phase: 'fast' | 'refine';
  p50TotalActive: number;
  p95TotalActive: number;
  activePercentiles?: Record<number, number>;
  personas: McPersonaStats[];
  avgPromptTokensPerSec: number;
  avgDecodeTokensPerSec: number;
}

type Phase = 'fast' | 'refine';
type McPercentile = 5 | 25 | 50 | 75 | 95 | 99;

type PersonaRuntime = {
  id: string;
  share: number;
  targetConcurrent: number;
  readTime: number;
  thinkTime: number;
  serviceTime: number; // GPU time per turn (ttft + decode)
  promptTokens: number;
  decodeTokens: number;
};

const DEFAULT_TIME_STEP = 0.5; // seconds

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function runMonteCarloFast(input: McInput, requestId: string, scenarioHash: string): McResult {
  const trials = Math.max(1, input.trials || 16);
  return runMonteCarloInternal('fast', { ...input, trials }, requestId, scenarioHash);
}

export function runMonteCarloRefine(input: McInput, requestId: string, scenarioHash: string): McResult {
  const trials = Math.max(1, input.trials || 64);
  return runMonteCarloInternal('refine', { ...input, trials }, requestId, scenarioHash);
}

// ---------------------------------------------------------------------------
// Core simulator
// ---------------------------------------------------------------------------

function runMonteCarloInternal(phase: Phase, input: McInput, requestId: string, scenarioHash: string): McResult {
  const personas = buildPersonas(input);
  const timeStep = DEFAULT_TIME_STEP;
  const steps = Math.max(1, Math.floor(input.durationSec / timeStep));

  const personaActives: Record<string, number[]> = {};
  const totalActive: number[] = [];
  let totalPromptTokensAllTrials = 0;
  let totalDecodeTokensAllTrials = 0;

  for (let trial = 0; trial < input.trials; trial += 1) {
    const rng = seededRandom(`${scenarioHash}-${requestId}-${phase}-${trial}`);
    const perPersonaQueues = personas.map(() => createTimeline(steps + 10, 0));

    // Event queues: index = step, value = count entering generating state at that step
    const generatingQueues = personas.map(() => createTimeline(steps + 10, 0));

    // Initialize arrays to collect actives per step for this trial
    personas.forEach((p) => {
      if (!personaActives[p.id]) personaActives[p.id] = [];
    });

    let promptTokensThisTrial = 0;
    let decodeTokensThisTrial = 0;

    for (let step = 0; step < steps; step += 1) {
      const t = step * timeStep;

      personas.forEach((persona, idx) => {
        const arrivals = poissonSample(arrivalRate(persona, input, timeStep), rng);

        // Schedule each new arrival through the state machine:
        // reading -> thinking -> generating
        for (let i = 0; i < arrivals; i += 1) {
          const readDone = step + Math.max(1, Math.round(persona.readTime / timeStep));
          const thinkDone = readDone + Math.max(1, Math.round(persona.thinkTime / timeStep));
          const genStart = thinkDone;
          const genEnd = genStart + Math.max(1, Math.round(persona.serviceTime / timeStep));
          // Add generation interval to generating queue
          incrementRange(generatingQueues[idx], genStart, genEnd);
        }

        if (arrivals > 0) {
          promptTokensThisTrial += arrivals * persona.promptTokens;
          decodeTokensThisTrial += arrivals * persona.decodeTokens;
        }

        const activeNow = generatingQueues[idx][step] || 0;
        personaActives[persona.id].push(activeNow);
      });

      // Total active at this step
      const activeStep = personas.reduce((sum, _, idx) => sum + (generatingQueues[idx][step] || 0), 0);
      totalActive.push(activeStep);
    }

    totalPromptTokensAllTrials += promptTokensThisTrial;
    totalDecodeTokensAllTrials += decodeTokensThisTrial;
  }

  const percentilesToCapture: McPercentile[] = [5, 25, 50, 75, 95, 99];
  const activePercentiles: Record<number, number> = {};
  percentilesToCapture.forEach((p) => {
    activePercentiles[p] = percentile(totalActive, p);
  });

  const p50TotalActive = activePercentiles[50] ?? percentile(totalActive, 50);
  const p95TotalActive = activePercentiles[95] ?? percentile(totalActive, 95);

  const totalSimTimeSec = input.trials * input.durationSec;
  const avgPromptTokensPerSec =
    totalSimTimeSec > 0 ? totalPromptTokensAllTrials / totalSimTimeSec : 0;
  const avgDecodeTokensPerSec =
    totalSimTimeSec > 0 ? totalDecodeTokensAllTrials / totalSimTimeSec : 0;

  const personaStats: McPersonaStats[] = personas.map((p) => {
    const series = personaActives[p.id] ?? [];
    const personaPercentiles: Record<McPercentile, number> = {
      5: percentile(series, 5),
      25: percentile(series, 25),
      50: percentile(series, 50),
      75: percentile(series, 75),
      95: percentile(series, 95),
      99: percentile(series, 99),
    };

    return {
      personaId: p.id,
      p50ActiveSessions: personaPercentiles[50],
      p95ActiveSessions: personaPercentiles[95],
      percentiles: personaPercentiles,
    };
  });

  return {
    requestId,
    scenarioHash,
    phase,
    p50TotalActive,
    p95TotalActive,
    activePercentiles,
    personas: personaStats,
    avgPromptTokensPerSec,
    avgDecodeTokensPerSec,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildPersonas(input: McInput): PersonaRuntime[] {
  const totalPop = input.personas.reduce((s, p) => s + Math.max(0, p.population), 0) || 1;
  const totalConcurrent = Math.max(0, input.concurrentUsersTotal);
  return input.personas.map((p) => {
    const share = Math.max(0, p.population) / totalPop;
    const targetConcurrent = totalConcurrent * share;
    const read = clampPositive(p.dutyCycle.readTimeSec, 0.1);
    const think = clampPositive(p.dutyCycle.thinkTimeSec, 0.1);
    const service = clampPositive(p.service.serviceTimeSec, 0.1);
    const promptTokens = Math.max(0, p.tokensPerTurn.input);
    const decodeTokens = Math.max(0, p.tokensPerTurn.output);
    return {
      id: p.personaId,
      share,
      targetConcurrent,
      readTime: read,
      thinkTime: think,
      serviceTime: service,
      promptTokens,
      decodeTokens,
    };
  });
}

function arrivalRate(persona: PersonaRuntime, input: McInput, timeStep: number): number {
  // One cycle = read + think + GPU service for a full conversation/session.
  const cycleSec = persona.readTime + persona.thinkTime + persona.serviceTime;
  const desiredActive = persona.targetConcurrent;

  // Arrival intensity needed to sustain the target concurrent sessions.
  // NOTE: tokensPerConversation and serviceTimeSec already include all turns,
  // so we do NOT multiply by conversationsPerUser here.
  const lambdaPerSec = cycleSec > 0 ? desiredActive / cycleSec : 0;

  // conversationsPerUser is already baked into the tokens/service inputs, so
  // convFactor must be 1 to avoid double counting turns.
  const convFactor = 1;

  return Math.max(0, lambdaPerSec * convFactor * timeStep);
}


function percentile(values: number[], p: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const rank = (p / 100) * (sorted.length - 1);
  const low = Math.floor(rank);
  const high = Math.ceil(rank);
  if (low === high) return sorted[low] || 0;
  const weight = rank - low;
  return (sorted[low] || 0) * (1 - weight) + (sorted[high] || 0) * weight;
}

function poissonSample(lambda: number, rng: () => number): number {
  if (lambda <= 0) return 0;
  const L = Math.exp(-lambda);
  let k = 0;
  let p = 1;
  do {
    k += 1;
    p *= rng();
  } while (p > L);
  return k - 1;
}

function seededRandom(seed: string): () => number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return () => {
    h += h << 13;
    h ^= h >>> 7;
    h += h << 3;
    h ^= h >>> 17;
    h += h << 5;
    return (h >>> 0) / 4294967296;
  };
}

function createTimeline(length: number, initial: number): number[] {
  return Array.from({ length }, () => initial);
}

function incrementRange(arr: number[], start: number, end: number): void {
  const s = Math.max(0, Math.floor(start));
  const e = Math.min(arr.length, Math.ceil(end));
  for (let i = s; i < e; i += 1) {
    arr[i] = (arr[i] ?? 0) + 1;
  }
}

function clampPositive(value: number, min: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, value);
}

// ---------------------------------------------------------------------------
// NOTE: This file is intentionally pure and does not depend on React, workers,
// or global state. Integrations should call runMonteCarloFast/refine directly.
// ---------------------------------------------------------------------------
